import type {
  CalendarEvent,
  CoachInsight,
  CompanyNameSuggestion,
  CompanyProfile,
  DiscoveryView,
  HintFocus,
  HistoryItem,
  Participant,
  TaskView,
  UserRole,
  UserStatsResponse,
} from "@scoach/types";
import { CUSTOM_QUESTION_PRESETS, HINT_FOCUS_LABELS } from "@scoach/types";
import { Modal, Spinner } from "@scoach/ui";
import {
  Brain,
  Chev,
  Doc,
  Inbox,
  Link as LinkIcon,
  Plus,
  Search,
  Spark,
  Trash,
  User as UserIcon,
} from "@scoach/ui/icons";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { isDriveUrl, runDriveImport } from "../../../lib/driveImport.ts";
import { api } from "../../../lib/http.ts";
import { authApi } from "../../auth/api.ts";
import { useAuthStore } from "../../auth/store.ts";
import { useAvailableRoles } from "../../../lib/roles.ts";
import { CompanyPicker } from "../../companies/CompanyPicker.tsx";
import { CompanyReportPanel } from "../../companies/CompanyReportPanel.tsx";
import { companiesApi } from "../../companies/api.ts";
import { CompanyLogo } from "../../discovery/CompanyLogo.tsx";
import { discoveryApi } from "../../discovery/api.ts";
import { preMeetingApi } from "../../premeeting/api.ts";
import { SuggestedQuestionsBanner } from "../../settings/components/SuggestedQuestionsBanner.tsx";
import { tasksApi } from "../../tasks/api.ts";
import { dashboardApi } from "../api.ts";
import { BulkChatModal } from "./BulkChatModal.tsx";
import { DashHeader } from "./DashHeader.tsx";
import { HistShareBtn } from "./HistShareBtn.tsx";
import { UpcomingMeetings } from "./UpcomingMeetings.tsx";

type Scope = "mine" | "shared" | "drafts";
type StageFilter = "all" | "discovery" | "qualification" | "negotiation";

/**
 * Pre-meeting context-doc picker for the quick-start modals (internal/external).
 * The meeting doesn't exist yet, so it just collects the user's chosen files +
 * Google Drive links locally; Dashboard.quickStart uploads them right after the
 * meeting is created. Mirrors the live "Context docs" panel.
 */
function QuickContextDocs({
  files,
  setFiles,
  links,
  setLinks,
}: {
  files: File[];
  setFiles: (f: File[]) => void;
  links: string[];
  setLinks: (l: string[]) => void;
}) {
  const [showLink, setShowLink] = useState(false);
  const [linkDraft, setLinkDraft] = useState("");
  const [linkErr, setLinkErr] = useState<string | null>(null);

  function addLink() {
    const url = linkDraft.trim();
    if (!url) return;
    if (!isDriveUrl(url)) {
      setLinkErr("Enter a Google Docs/Drive link");
      return;
    }
    setLinks([...links, url]);
    setLinkDraft("");
    setShowLink(false);
    setLinkErr(null);
  }

  return (
    <div className="qm-field">
      <span className="qm-label">
        Documents about the meeting{" "}
        <span style={{ color: "var(--text-3)", fontWeight: 400 }}>(optional)</span>
      </span>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <label className="ghost-btn xs" style={{ cursor: "pointer" }}>
          <Plus size={13} /> Upload file
          <input
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) setFiles([...files, ...Array.from(e.target.files)]);
              e.target.value = "";
            }}
          />
        </label>
        <button
          type="button"
          className="ghost-btn xs"
          onClick={() => setShowLink((v) => !v)}
          title="Paste a link to a file in your Google Drive"
        >
          <LinkIcon size={13} /> Add Drive link
        </button>
      </div>

      {showLink && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 4 }}>
            Paste a <strong>Google Drive / Docs share link</strong> (make sure it's shared so SellSynq can read it):
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              className="setup-input"
              style={{ flex: 1, minWidth: 0 }}
              placeholder="https://docs.google.com/…  or  https://drive.google.com/…"
              value={linkDraft}
              autoFocus
              onChange={(e) => setLinkDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addLink();
                } else if (e.key === "Escape") {
                  setShowLink(false);
                }
              }}
            />
            <button
              type="button"
              className="pill-btn sm"
              disabled={!linkDraft.trim()}
              onClick={addLink}
            >
              Add
            </button>
          </div>
        </div>
      )}

      {linkErr && (
        <div style={{ fontSize: 12, color: "var(--gc-red)", marginTop: 4 }}>{linkErr}</div>
      )}

      {(files.length > 0 || links.length > 0) && (
        <ul
          style={{
            listStyle: "none",
            margin: "8px 0 0",
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {files.map((f, i) => (
            <li
              key={`f-${f.name}-${i}`}
              style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5 }}
            >
              <Doc size={13} />
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                {f.name}
              </span>
              <button
                type="button"
                className="ghost-btn xs danger"
                onClick={() => setFiles(files.filter((_, j) => j !== i))}
              >
                <Trash size={12} />
              </button>
            </li>
          ))}
          {links.map((url, i) => (
            <li
              key={`l-${url}-${i}`}
              style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5 }}
            >
              <LinkIcon size={13} />
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                {url}
              </span>
              <button
                type="button"
                className="ghost-btn xs danger"
                onClick={() => setLinks(links.filter((_, j) => j !== i))}
              >
                <Trash size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatHistoryDate(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  return { date, time };
}

export function Dashboard() {
  const nav = useNavigate();
  const user = useAuthStore((s) => s.user);
  const availableRoles = useAvailableRoles();
  const displayName = useAuthStore((s) => s.displayName);
  const storeEmail = useAuthStore((s) => s.email);
  const firstName = (user?.name ?? displayName ?? storeEmail?.split("@")[0] ?? "").split(" ")[0];

  // One-time hint pointing users to the custom-question templates feature.
  const hasUserTemplates = Object.keys(user?.settings.customQuestionSets ?? {}).some(
    (k) => !CUSTOM_QUESTION_PRESETS[k],
  );
  const [tmplHintDismissed, setTmplHintDismissed] = useState(
    () => localStorage.getItem("sally.tmplHintDismissed") === "1",
  );

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<StageFilter>("all");
  const [scope, setScope] = useState<Scope>("mine");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  // Label filter — meetings matching ANY selected label are shown; also drives the
  // "ask about these labels" flow.
  const [selectedLabels, setSelectedLabels] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  const [allHistory, setAllHistory] = useState<HistoryItem[]>([]);
  // Cursor for paging older "mine" meetings from the server (we load 10 first for a
  // fast dashboard, then fetch more on demand). Undefined = nothing more to load.
  const [moreCursor, setMoreCursor] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const [stats, setStats] = useState<UserStatsResponse | null>(null);
  const [creating, setCreating] = useState(false);
  const [allTasks, setAllTasks] = useState<TaskView[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [insights, setInsights] = useState<CoachInsight[]>([]);
  const [discoveries, setDiscoveries] = useState<DiscoveryView[]>([]);

  useEffect(() => {
    // Load only the most recent 10 of the user's own meetings up front so the page
    // paints fast; older ones load on demand via "Load more" (see loadMoreHistory).
    Promise.all([
      dashboardApi.fetchHistory({ scope: "mine", limit: 10 }),
      dashboardApi.fetchHistory({ scope: "shared", limit: 10 }),
      dashboardApi.fetchStats(),
    ])
      .then(([mine, shared, s]) => {
        setAllHistory([...mine.items, ...shared.items]);
        setMoreCursor(mine.nextCursor);
        setStats(s);
      })
      .catch(() => {});

    tasksApi
      .list()
      .then((res) => setAllTasks(res.items))
      .catch(() => {});

    discoveryApi
      .list()
      .then(setDiscoveries)
      .catch(() => {});
    dashboardApi
      .fetchInsights()
      .then((items) => setInsights(items))
      .catch(() => {});
  }, []);

  // Fetch the next page of older "mine" meetings and append them (deduped).
  async function loadMoreHistory() {
    if (!moreCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await dashboardApi.fetchHistory({ scope: "mine", limit: 30, cursor: moreCursor });
      setAllHistory((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        return [...prev, ...res.items.filter((m) => !seen.has(m.id))];
      });
      setMoreCursor(res.nextCursor);
    } catch {
      // leave the button in place so the user can retry
    } finally {
      setLoadingMore(false);
    }
  }

  const filtered = useMemo(() => {
    const items = allHistory.filter((m) => {
      const isShared = !!m.sharedBy;
      const isDraft = m.status === "draft";
      const isSim = m.meetingType === "simulation";
      if (scope === "mine" && (isShared || isDraft || isSim)) return false;
      if (scope === "shared" && !isShared) return false;
      if (scope === "drafts" && !isDraft) return false;
      if (filter !== "all" && m.stage.toLowerCase() !== filter) return false;
      if (typeFilter !== "all") {
        // Undefined meetingType is treated as a plain sales meeting.
        const t = m.meetingType ?? "sales";
        if (t !== typeFilter) return false;
      }
      if (selectedLabels.size > 0) {
        // Match if the meeting carries ANY of the selected labels (case-insensitive).
        const has = (m.tags ?? []).some((t) => selectedLabels.has(t.toLowerCase()));
        if (!has) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const hay = `${m.client} ${m.title} ${(m.tags ?? []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    items.sort((a, b) => {
      const aScheduled = a.scheduledAt && new Date(a.scheduledAt) > new Date() ? 1 : 0;
      const bScheduled = b.scheduledAt && new Date(b.scheduledAt) > new Date() ? 1 : 0;
      const aLive = a.status === "live" ? 1 : 0;
      const bLive = b.status === "live" ? 1 : 0;
      const aPriority = aLive * 3 + aScheduled * 2;
      const bPriority = bLive * 3 + bScheduled * 2;
      if (bPriority !== aPriority) return bPriority - aPriority;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
    return items;
  }, [allHistory, scope, filter, typeFilter, search, selectedLabels]);

  // The set of labels in the user's own (non-shared) meetings, for the filter bar —
  // sorted by frequency (most-used first), then alphabetically.
  const allLabels = useMemo(() => {
    const counts = new Map<string, { label: string; n: number }>();
    for (const m of allHistory) {
      for (const t of m.tags ?? []) {
        const key = t.toLowerCase();
        const cur = counts.get(key);
        if (cur) cur.n += 1;
        else counts.set(key, { label: t, n: 1 });
      }
    }
    return [...counts.values()]
      .sort((a, b) => b.n - a.n || a.label.localeCompare(b.label))
      .map((c) => c.label);
  }, [allHistory]);

  function toggleLabel(label: string) {
    setSelectedLabels((prev) => {
      const next = new Set(prev);
      const key = label.toLowerCase();
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Reset to the first page whenever the filters/search change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset on filter change
  useEffect(() => {
    setPage(1);
  }, [scope, filter, typeFilter, search, selectedLabels]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageClamped = Math.min(page, totalPages);
  const paged = useMemo(
    () => filtered.slice((pageClamped - 1) * PAGE_SIZE, pageClamped * PAGE_SIZE),
    [filtered, pageClamped],
  );

  const myCount = useMemo(
    () =>
      allHistory.filter(
        (m) => !m.sharedBy && m.status !== "draft" && m.meetingType !== "simulation",
      ).length,
    [allHistory],
  );
  const sharedCount = useMemo(() => allHistory.filter((m) => !!m.sharedBy).length, [allHistory]);
  const draftCount = useMemo(
    () => allHistory.filter((m) => m.status === "draft").length,
    [allHistory],
  );
  const openTaskCount = useMemo(() => allTasks.filter((t) => !t.done).length, [allTasks]);

  const byClient = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const h of allHistory) counts[h.client] = (counts[h.client] ?? 0) + 1;
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [allHistory]);

  const tasksByMeeting = useMemo(() => {
    const map = new Map<string, TaskView[]>();
    for (const t of allTasks) {
      const arr = map.get(t.meetingId) ?? [];
      arr.push(t);
      map.set(t.meetingId, arr);
    }
    return map;
  }, [allTasks]);

  function toggleTaskDone(task: TaskView) {
    const next = !task.done;
    setAllTasks((prev) => prev.map((t) => (t.taskId === task.taskId ? { ...t, done: next } : t)));
    void tasksApi.updateTask(task.taskId, { done: next }).catch(() => {
      setAllTasks((prev) =>
        prev.map((t) => (t.taskId === task.taskId ? { ...t, done: !next } : t)),
      );
    });
  }

  function defaultTitle(): string {
    const d = new Date();
    const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const time = d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `Meeting — ${date}, ${time}`;
  }

  /** Helper to determine if an email belongs to the user's internal organization. */
  function isInternalEmail(email: string): boolean {
    const userEmail = (user?.email ?? storeEmail ?? "").toLowerCase().trim();
    const userDomain = userEmail.includes("@") ? (userEmail.split("@")[1] || "") : "";
    const isGeneric = ["gmail.com", "googlemail.com", "outlook.com", "hotmail.com", "yahoo.com", "icloud.com"].includes(userDomain);
    const e = email.toLowerCase().trim();
    if (!userDomain || isGeneric) return e === userEmail;
    if (userDomain === "google.com") {
      return e.endsWith("@google.com") || e.endsWith("@altostrat.com");
    }
    return e.endsWith(`@${userDomain}`);
  }

  /** Start a meeting from a calendar event via the quick-meeting popup. A meeting with
   * any attendee outside the user's organization is treated as EXTERNAL (customer).
   * The user confirms role/goal and goes live. */
  function startFromCalendar(event: CalendarEvent) {
    const others = event.attendees.filter((a) => !a.self && !a.resource);
    const ext = others.find((a) => !isInternalEmail(a.email));
    // Seed the meeting's participants from the calendar attendees so the summary +
    // follow-up email use their real names (and can confirm who was expected).
    const palette = ["#6366f1", "#4f46e5", "#475569", "#64748b", "#818cf8", "#3b82f6", "#4338ca"];
    const participants: Participant[] = others.map((a, i) => {
      const name = a.displayName || a.email.split("@")[0] || a.email;
      const parts = name.trim().split(/\s+/);
      const initials =
        ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() ||
        name.slice(0, 2).toUpperCase();
      return {
        name,
        role: a.email,
        side: isInternalEmail(a.email) ? "rep" : "client",
        color: palette[i % palette.length] as string,
        initials,
      };
    });
    setQuickParticipants(participants);
    setQuickGoal(event.summary || "");
    setQuickError("");
    if (ext) {
      const domain = ext.email.split("@")[1]?.split(".")[0] ?? "";
      const account = domain ? domain.charAt(0).toUpperCase() + domain.slice(1) : "";
      setQuickKind("external");
      setQuickName(account);
      setQuickProfile(null);
    } else {
      setQuickKind("internal");
    }
    setQuickOpen(true);
  }

  /** Run a Discovery on the people from a calendar invite — seed it with the client
   * company (from the external attendee's domain) and the participant names, then open
   * the discovery so its research covers who's in the meeting. */
  async function startDiscoveryFromCalendar(event: CalendarEvent) {
    const others = event.attendees.filter((a) => !a.self && !a.resource);
    const ext = others.find((a) => !isInternalEmail(a.email));
    const website = ext?.email.split("@")[1] ?? "";
    const label = website ? website.split(".")[0] : "";
    const company = label
      ? label.charAt(0).toUpperCase() + label.slice(1)
      : event.summary || "Meeting";
    const peopleList = others.map((a) => `${a.displayName || a.email.split("@")[0]} <${a.email}>`);
    const freeText = `Prep for "${event.summary}". People on the invite to research: ${peopleList.join(", ")}.`;
    setCreating(true);
    try {
      const { id } = await discoveryApi.create({
        name: company,
        ...(website ? { website } : {}),
        freeText,
        people: peopleList,
      });
      nav({ to: "/discovery/$id", params: { id } });
    } finally {
      setCreating(false);
    }
  }

  /** Start a meeting from a saved discovery (pre-filled company + notes/docs). */
  async function startFromDiscovery(id: string) {
    setCreating(true);
    try {
      const { meetingId } = await discoveryApi.startMeeting(id, "setup");
      nav({ to: "/meetings/$id/setup", params: { id: meetingId } });
    } finally {
      setCreating(false);
    }
  }

  // --- Quick live: ask internal/external, (external) the company, then go live ---
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickKind, setQuickKind] = useState<"internal" | "external" | null>(null);
  const [quickName, setQuickName] = useState("");
  const [quickProfile, setQuickProfile] = useState<CompanyProfile | null>(null);
  // Attendees pulled from a calendar event, seeded onto the meeting so the summary
  // + follow-up email use the real participant names (and can confirm who attended).
  const [quickParticipants, setQuickParticipants] = useState<Participant[]>([]);
  const [quickResearching, setQuickResearching] = useState(false);
  const [quickError, setQuickError] = useState("");
  const [companySuggestions, setCompanySuggestions] = useState<CompanyNameSuggestion[]>([]);
  // Per-meeting role (defaults to the user's last choice / profile role) + optional goal.
  const [quickRole, setQuickRole] = useState<UserRole | "">("");
  const [quickGoal, setQuickGoal] = useState("");
  const [quickQKey, setQuickQKey] = useState<string>("");
  // Optional meeting focus (Training / Technical / Executive / …). "" = default (Standard).
  const [quickFocus, setQuickFocus] = useState<HintFocus | "">("");
  // Optional client industry (external) + pre-meeting context docs (both kinds).
  const [quickIndustry, setQuickIndustry] = useState("");
  const [quickFiles, setQuickFiles] = useState<File[]>([]);
  const [quickLinks, setQuickLinks] = useState<string[]>([]);
  // Give the company-intel results/graphs more room by shrinking the setup fields.
  const [intelExpanded, setIntelExpanded] = useState(false);
  const effectiveQuickRole: UserRole = (quickRole ||
    user?.settings?.lastChosenRole ||
    user?.role ||
    "Customer Engineer") as UserRole;
  // Prep-question set: explicit choice, else the role's preset if one exists.
  const effectiveQKey =
    quickQKey || (CUSTOM_QUESTION_PRESETS[effectiveQuickRole] ? effectiveQuickRole : "");

  useEffect(() => {
    companiesApi
      .list()
      .then(setCompanySuggestions)
      .catch(() => {});
  }, []);

  function quickLive() {
    setQuickKind(null);
    setQuickName("");
    setQuickProfile(null);
    setQuickError("");
    setQuickParticipants([]);
    setQuickIndustry("");
    setQuickFocus("");
    setQuickFiles([]);
    setQuickLinks([]);
    setQuickOpen(true);
  }

  useEffect(() => {
    try {
      if (sessionStorage.getItem("sellsynq:auto-quick-live") === "1") {
        sessionStorage.removeItem("sellsynq:auto-quick-live");
        quickLive();
      }
    } catch {}
    const onQuick = () => quickLive();
    window.addEventListener("sellsynq:open-quick-meeting", onQuick);
    return () => window.removeEventListener("sellsynq:open-quick-meeting", onQuick);
  }, []);

  /** Upload the quick-start context docs (files + Drive links) onto a just-created
   * meeting, before going live, so hints use them from the start. Best-effort. */
  async function uploadQuickDocs(meetingId: string) {
    try {
      if (quickFiles.length > 0) await preMeetingApi.uploadContext(meetingId, quickFiles);
    } catch {
      /* best-effort — don't block starting the meeting */
    }
    for (const url of quickLinks) {
      try {
        await runDriveImport((token) => preMeetingApi.importContextLink(meetingId, url, token));
      } catch {
        /* skip a link that fails; the meeting still starts */
      }
    }
  }

  // Research runs only on an explicit action — pressing Enter in the company field
  // or picking a suggestion — never while typing (see CompanyNameAutocomplete).
  async function quickResearch(refresh = false, website?: string, nameArg?: string) {
    const name = (nameArg ?? quickName).trim();
    if (!name) return;
    setQuickResearching(true);
    setQuickError("");
    try {
      const profile = await companiesApi.research({
        name,
        refresh,
        ...(website ? { website } : {}),
      });
      setQuickProfile(profile);
    } catch (err) {
      setQuickError((err as Error).message || `Couldn't research "${name}". Try again.`);
    } finally {
      setQuickResearching(false);
    }
  }

  async function quickStart(kindOverride?: "internal" | "external") {
    setCreating(true);
    try {
      const internal = (kindOverride ?? quickKind) === "internal";
      const name = quickName.trim();
      const industry = quickIndustry.trim();
      const account = internal
        ? { name: "Internal meeting", isInternal: true }
        : {
            name,
            ...(quickProfile ? { companyProfileSlug: quickProfile.slug } : {}),
            ...(industry ? { industry } : {}),
          };
      const goal = quickGoal.trim();
      const m = await dashboardApi.createMeeting({
        account,
        title: defaultTitle(),
        stage: "Intro",
        autoTitled: true,
        role: effectiveQuickRole,
        ...(goal ? { goal } : {}),
        ...(effectiveQKey ? { customQuestionKey: effectiveQKey } : {}),
      });
      // Seed calendar attendees as the meeting's participants (used by the summary +
      // follow-up email, and to confirm who was expected). Best-effort, owner-only.
      if (quickParticipants.length > 0) {
        await api(`/meetings/${m.id}`, {
          method: "PATCH",
          body: { participants: quickParticipants },
        }).catch(() => {});
      }
      // Optional meeting focus (Training / Technical / Executive / …) chosen in the popup.
      if (quickFocus) {
        await api(`/meetings/${m.id}/hint-focus`, {
          method: "PATCH",
          body: { focus: quickFocus },
        }).catch(() => {});
      }
      // Attach any pre-meeting context docs (files + Drive links) before going live.
      await uploadQuickDocs(m.id);
      // Remember the chosen role as the default for next time.
      void authApi.updateSettings({ lastChosenRole: effectiveQuickRole }).catch(() => {});
      nav({ to: "/meetings/$id/live", params: { id: m.id } });
    } finally {
      setCreating(false);
    }
  }

  function openMeeting(id: string, status?: string, endedAt?: string) {
    if (status === "draft") {
      nav({ to: "/meetings/$id/setup", params: { id } });
    } else if (status === "live" && !endedAt) {
      // Only a meeting that is live AND has never been ended goes to the live screen.
      // A resumed meeting keeps endedAt, so it opens its summary instead of being stuck live.
      nav({ to: "/meetings/$id/live", params: { id } });
    } else {
      nav({ to: "/meetings/$id/summary", params: { id } });
    }
  }

  function deleteMeeting(id: string, client: string) {
    if (!confirm(`Delete meeting with ${client || "this client"}? This cannot be undone.`)) return;
    setAllHistory((prev) => prev.filter((m) => m.id !== id));
    void dashboardApi.deleteMeeting(id).catch(() => {
      dashboardApi
        .fetchHistory({})
        .then((r) => setAllHistory(r.items))
        .catch(() => {});
    });
  }

  // --- Bulk selection + delete ---
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkChatOpen, setBulkChatOpen] = useState(false);
  const [labelChatOpen, setLabelChatOpen] = useState(false);
  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function clearSelection() {
    setSelectedIds(new Set());
  }
  async function bulkDelete() {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (
      !confirm(`Delete ${ids.length} meeting${ids.length === 1 ? "" : "s"}? This cannot be undone.`)
    )
      return;
    setBulkDeleting(true);
    setAllHistory((prev) => prev.filter((m) => !selectedIds.has(m.id)));
    try {
      await Promise.allSettled(ids.map((id) => dashboardApi.deleteMeeting(id)));
    } finally {
      clearSelection();
      setBulkDeleting(false);
      // Reconcile with the server in case any delete failed.
      dashboardApi
        .fetchHistory({})
        .then((r) => setAllHistory(r.items))
        .catch(() => {});
    }
  }

  // --- Inline editing for meeting title and client ---
  const [editingField, setEditingField] = useState<{
    id: string;
    field: "title" | "client";
  } | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [clientSuggestions, setClientSuggestions] = useState<string[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(-1);
  const editInputRef = useRef<HTMLInputElement>(null);

  const knownClients = useMemo(() => {
    const names = new Set<string>();
    for (const m of allHistory) {
      if (m.client && m.client !== "—") names.add(m.client);
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [allHistory]);

  function startEditing(e: React.MouseEvent, id: string, field: "title" | "client", value: string) {
    e.stopPropagation();
    e.preventDefault();
    setEditingField({ id, field });
    setEditDraft(value);
    setClientSuggestions([]);
    setSelectedSuggestion(-1);
  }

  const commitEdit = useCallback(() => {
    if (!editingField) return;
    const text = editDraft.trim();
    if (!text) {
      setEditingField(null);
      return;
    }
    const { id, field } = editingField;
    const original = allHistory.find((m) => m.id === id);
    if (!original) {
      setEditingField(null);
      return;
    }
    const oldVal = field === "title" ? original.title : original.client;
    if (text === oldVal) {
      setEditingField(null);
      return;
    }

    setAllHistory((prev) =>
      prev.map((m) =>
        m.id === id
          ? { ...m, [field]: text, ...(field === "client" ? { avatar: original.avatar } : {}) }
          : m,
      ),
    );
    setEditingField(null);

    const patch = field === "title" ? { title: text } : { account: { name: text } };
    void dashboardApi.patchMeeting(id, patch).catch(() => {
      setAllHistory((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: oldVal } : m)));
    });
  }, [editingField, editDraft, allHistory]);

  function handleEditKeyDown(e: React.KeyboardEvent) {
    // The inline edit input is nested inside the clickable `.hist-row` <button>.
    // Without this, keys like Space bubble up and activate the button, navigating
    // out of / into the meeting mid-rename. Stop the event from reaching it so
    // Space (and every other key) is handled by the input alone.
    e.stopPropagation();
    if (editingField?.field === "client" && clientSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedSuggestion((i) => Math.min(i + 1, clientSuggestions.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedSuggestion((i) => Math.max(i - 1, -1));
        return;
      }
      if (e.key === "Enter" && selectedSuggestion >= 0) {
        e.preventDefault();
        setEditDraft(clientSuggestions[selectedSuggestion]!);
        setClientSuggestions([]);
        return;
      }
    }
    if (e.key === "Enter") commitEdit();
    if (e.key === "Escape") setEditingField(null);
  }

  function handleClientInput(value: string) {
    setEditDraft(value);
    setSelectedSuggestion(-1);
    if (value.trim().length > 0) {
      const lower = value.toLowerCase();
      setClientSuggestions(knownClients.filter((c) => c.toLowerCase().includes(lower)));
    } else {
      setClientSuggestions([]);
    }
  }

  function selectSuggestion(name: string) {
    setEditDraft(name);
    setClientSuggestions([]);
  }

  async function startSimulation(parentId: string) {
    try {
      const sim = await dashboardApi.createSimulation(parentId);
      nav({ to: "/meetings/$id/live", params: { id: sim.id } });
    } catch {
      alert("Failed to create simulation. Please try again.");
    }
  }

  async function continueMeeting(id: string) {
    try {
      await dashboardApi.resumeMeeting(id);
      nav({ to: "/meetings/$id/live", params: { id } });
    } catch {
      alert("Failed to continue meeting. Please try again.");
    }
  }

  return (
    <div className="dash">
      <DashHeader onStartNew={quickLive} />

      <main className="dash-main">
        {/* Executive Top Header Bar */}
        <header className="dash-top-bar">
          <div className="dash-top-welcome">
            <div className="dash-kicker">Revenue Intelligence Workspace</div>
            <div className="dash-title-row">
              <h1 className="dash-title">
                Welcome back{firstName ? `, ${firstName}` : ""}
              </h1>
              {(user?.tier === "gold" || user?.tier === "silver") && (
                <span className={`dash-tier-badge tier-${user.tier}`}>
                  {user.tier === "gold" ? "🥇 Gold Partner" : "🥈 Silver Partner"}
                </span>
              )}
            </div>
            <p className="dash-sub">
              {openTaskCount > 0
                ? `${openTaskCount} pending action item${openTaskCount === 1 ? "" : "s"} across active accounts.`
                : "All caught up · 0 pending action items."}
            </p>
          </div>

          <div className="dash-top-actions">
            <button
              type="button"
              className="dash-action-btn secondary"
              onClick={() => nav({ to: "/discovery" })}
              title="Deep-research a company before your meeting — founders, competitors, market, GCP fit"
            >
              <Search size={15} />
              <span>Company Discovery</span>
            </button>
            <button
              type="button"
              className="dash-action-btn primary"
              onClick={quickLive}
              disabled={creating}
              title="Go straight to a live meeting, skipping the setup wizard"
            >
              <Spark size={15} />
              <span>Quick Live Meeting</span>
            </button>
          </div>
        </header>

        {/* Executive 4-KPI Row */}
        <section className="dash-kpi-row">
          <div className="dash-kpi-card">
            <div className="kpi-header">
              <span className="kpi-label">Meetings This Week</span>
              <span className="kpi-badge cyan">{stats?.trend.meetings ? `+${stats.trend.meetings}` : "Active"}</span>
            </div>
            <div className="kpi-value">{stats?.thisWeek.meetings ?? allHistory.length}</div>
            <div className="kpi-sub">Total recorded sessions</div>
          </div>

          <div className="dash-kpi-card">
            <div className="kpi-header">
              <span className="kpi-label">Avg. Confidence</span>
              <span className="kpi-badge emerald">
                {stats?.thisWeek.avgConfidence != null && stats.thisWeek.avgConfidence >= 0.7 ? "High" : "Optimal"}
              </span>
            </div>
            <div className="kpi-value">
              {stats?.thisWeek.avgConfidence != null
                ? `${Math.round(stats.thisWeek.avgConfidence * 100)}%`
                : "—"}
            </div>
            <div className="kpi-sub">AI objection handling score</div>
          </div>

          <div className="dash-kpi-card">
            <div className="kpi-header">
              <span className="kpi-label">Open Action Items</span>
              <span className={`kpi-badge ${openTaskCount > 0 ? "amber" : "emerald"}`}>
                {openTaskCount > 0 ? `${openTaskCount} pending` : "All Done"}
              </span>
            </div>
            <div className="kpi-value">{stats?.thisWeek.openTasks ?? openTaskCount}</div>
            <div className="kpi-sub">Follow-ups across meetings</div>
          </div>

          <div className="dash-kpi-card">
            <div className="kpi-header">
              <span className="kpi-label">Coach Hints Acted On</span>
              <span className="kpi-badge violet">Real-time</span>
            </div>
            <div className="kpi-value">{stats?.thisWeek.hintsActedPct ?? 0}%</div>
            <div className="kpi-sub">Live coaching adoption rate</div>
          </div>
        </section>

        {/* Banners */}
        <div className="dash-banners-wrap">
          <SuggestedQuestionsBanner compact />
          {!hasUserTemplates && !tmplHintDismissed && (
            <div className="dash-tmpl-hint">
              <div className="dash-tmpl-text">
                <span className="dash-tmpl-tag">New</span>
                <span>
                  Create your own <strong>question templates</strong> — SellSynq answers them automatically on every meeting summary.
                </span>
              </div>
              <div className="dash-tmpl-actions">
                <button
                  type="button"
                  className="pill-btn xs primary"
                  onClick={() => nav({ to: "/settings", search: { tab: "summary" } })}
                >
                  Set up
                </button>
                <button
                  type="button"
                  className="ghost-btn xs"
                  onClick={() => {
                    localStorage.setItem("sally.tmplHintDismissed", "1");
                    setTmplHintDismissed(true);
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Discoveries Strip */}
        {discoveries.length > 0 && (
          <section className="dash-discovery-strip">
            <div className="dash-discovery-head">
              <div className="dash-discovery-title">
                <Search size={15} />
                <span>Account Discoveries &amp; Research</span>
              </div>
              <button
                type="button"
                className="ghost-btn xs"
                onClick={() => nav({ to: "/discovery" })}
              >
                + New discovery
              </button>
            </div>
            <div className="dash-discovery-row">
              {discoveries.slice(0, 8).map((d) => (
                <div key={d.discovery.id} className="dash-discovery-card">
                  <button
                    type="button"
                    className="dash-discovery-open"
                    onClick={() => nav({ to: "/discovery/$id", params: { id: d.discovery.id } })}
                    title="Open discovery"
                  >
                    <CompanyLogo
                      name={d.discovery.companyName}
                      domain={d.profile?.website ?? d.discovery.website}
                      logoUrl={d.profile?.logoUrl}
                      size={34}
                    />
                    <span className="dash-discovery-name">{d.discovery.companyName}</span>
                  </button>
                  <button
                    type="button"
                    className="pill-btn xs"
                    onClick={() => void startFromDiscovery(d.discovery.id)}
                    disabled={creating}
                  >
                    ▶ Start meeting
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 2-Column Intelligence Grid */}
        <div className="dash-content-grid">
          {/* Primary Column */}
          <div className="dash-col-primary">
            {/* Upcoming meetings (from Google Calendar) */}
            <UpcomingMeetings
              onStartCoaching={startFromCalendar}
              onDiscover={startDiscoveryFromCalendar}
            />

            {/* History */}
            <section className="dash-history">
          <div className="dash-history-head">
            <h2 className="dash-h2">Meeting history</h2>
            <div className="dash-history-actions">
              <div className="seg seg-tabs">
                <button
                  type="button"
                  className={scope === "mine" ? "on" : ""}
                  onClick={() => setScope("mine")}
                >
                  <UserIcon size={12} /> My meetings <span className="seg-count">{myCount}</span>
                </button>
                <button
                  type="button"
                  className={scope === "shared" ? "on" : ""}
                  onClick={() => setScope("shared")}
                >
                  <Inbox size={12} /> Shared with me{" "}
                  <span className="seg-count">{sharedCount}</span>
                </button>
                <button
                  type="button"
                  className={scope === "drafts" ? "on" : ""}
                  onClick={() => setScope("drafts")}
                >
                  Drafts <span className="seg-count">{draftCount}</span>
                </button>
              </div>
              <div className="search-box">
                <Search size={14} />
                <input
                  type="search"
                  placeholder="Search by client, topic, tag…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="seg seg-sm">
                <button
                  type="button"
                  className={filter === "all" ? "on" : ""}
                  onClick={() => setFilter("all")}
                >
                  All
                </button>
                <button
                  type="button"
                  className={filter === "discovery" ? "on" : ""}
                  onClick={() => setFilter("discovery")}
                >
                  Discovery
                </button>
                <button
                  type="button"
                  className={filter === "qualification" ? "on" : ""}
                  onClick={() => setFilter("qualification")}
                >
                  Qualification
                </button>
                <button
                  type="button"
                  className={filter === "negotiation" ? "on" : ""}
                  onClick={() => setFilter("negotiation")}
                >
                  Negotiation
                </button>
              </div>
              <div className="seg seg-sm">
                {(
                  [
                    ["all", "All types"],
                    ["sales", "Sales"],
                    ["technical", "Technical"],
                    ["interview", "Interview"],
                    ["onboarding", "Onboarding"],
                    ["review", "Review"],
                  ] as const
                ).map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    className={typeFilter === val ? "on" : ""}
                    onClick={() => setTypeFilter(val)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Label filter — click labels to narrow the list; then ask Sally across every
              meeting carrying the selected labels. Only shown once meetings have labels. */}
          {allLabels.length > 0 && (
            <div className="hist-labelbar">
              <span className="hist-labelbar-title">Labels:</span>
              {allLabels.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={`hist-label-chip${selectedLabels.has(l.toLowerCase()) ? " on" : ""}`}
                  onClick={() => toggleLabel(l)}
                >
                  {l}
                </button>
              ))}
              {selectedLabels.size > 0 && (
                <>
                  <button
                    type="button"
                    className="pill-btn xs primary"
                    onClick={() => setLabelChatOpen(true)}
                    title="Ask SellSynq a question across every meeting with the selected labels"
                  >
                    <Spark size={13} /> Ask about these labels
                  </button>
                  <button
                    type="button"
                    className="ghost-btn xs"
                    onClick={() => setSelectedLabels(new Set())}
                  >
                    Clear
                  </button>
                </>
              )}
            </div>
          )}

          {/* Always visible so the "ask across meetings" and delete actions are
              discoverable; the actions stay disabled until at least one meeting is ticked. */}
          <div className="hist-bulkbar">
            <span>
              {selectedIds.size > 0
                ? `${selectedIds.size} selected`
                : "Select meetings to delete, or ask SellSynq across them"}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              {selectedIds.size > 0 && (
                <button type="button" className="ghost-btn xs" onClick={clearSelection}>
                  Clear
                </button>
              )}
              <button
                type="button"
                className="pill-btn xs primary"
                onClick={() => setBulkChatOpen(true)}
                disabled={selectedIds.size === 0}
              >
                <Spark size={13} /> Ask about selected
              </button>
              <button
                type="button"
                className="pill-btn xs danger"
                onClick={() => void bulkDelete()}
                disabled={bulkDeleting || selectedIds.size === 0}
              >
                <Trash size={13} />{" "}
                {bulkDeleting
                  ? "Deleting…"
                  : selectedIds.size > 0
                    ? `Delete ${selectedIds.size}`
                    : "Delete"}
              </button>
            </div>
          </div>

          <div className="hist-table">
            <div className="hist-row hist-head-row">
              <button
                type="button"
                className="hist-select"
                aria-label="Select all on this page"
                onClick={() => {
                  const on = !(paged.length > 0 && paged.every((m) => selectedIds.has(m.id)));
                  setSelectedIds((prev) => {
                    const next = new Set(prev);
                    for (const m of paged) {
                      if (on) next.add(m.id);
                      else next.delete(m.id);
                    }
                    return next;
                  });
                }}
              >
                <input
                  type="checkbox"
                  checked={paged.length > 0 && paged.every((m) => selectedIds.has(m.id))}
                  readOnly
                  tabIndex={-1}
                  style={{ pointerEvents: "none" }}
                />
              </button>
              <div>Date</div>
              <div>Client</div>
              <div>Meeting</div>
              <div>Stage</div>
              <div>Actions</div>
              <div>Labels</div>
              <div></div>
            </div>
            {paged.map((m) => {
              const { date, time } = formatHistoryDate(m.date);
              const meetingTasks = tasksByMeeting.get(m.id) ?? [];
              const actionCount = m.actionItemCount ?? meetingTasks.length;
              const isExpanded = expandedId === m.id;
              return (
                <div key={m.id} className="hist-row-group">
                  {/* Row is a div (not a <button>) so the inline rename <input>
                      nested inside it doesn't let Space activate the row and
                      navigate away mid-edit. Opens on click or Enter only. */}
                  <div
                    className="hist-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => openMeeting(m.id, m.status, m.endedAt)}
                    onKeyDown={(e) => {
                      // Never navigate while inline-editing a field in this row.
                      if (editingField?.id === m.id) return;
                      if (e.key === "Enter") {
                        e.preventDefault();
                        openMeeting(m.id, m.status, m.endedAt);
                      } else if (e.key === " ") {
                        // Swallow Space (would otherwise scroll) without navigating.
                        e.preventDefault();
                      }
                    }}
                  >
                    {/* The whole cell toggles selection (a generous hit area) so a
                        near-miss selects the row instead of opening the meeting. */}
                    <button
                      type="button"
                      className="hist-select"
                      aria-label={`Select meeting with ${m.client}`}
                      aria-pressed={selectedIds.has(m.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelected(m.id);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(m.id)}
                        readOnly
                        tabIndex={-1}
                        style={{ pointerEvents: "none" }}
                      />
                    </button>
                    <div className="hist-date-col">
                      <div className="hist-date mono">{date}</div>
                      <div className="hist-time mono">
                        {time} · {m.duration}
                      </div>
                    </div>
                    <div className="hist-client" onClick={(e) => e.stopPropagation()}>
                      <div className="hist-avatar" style={{ background: m.avatar }}>
                        {m.client[0]}
                      </div>
                      <div style={{ position: "relative", minWidth: 0, flex: 1 }}>
                        {editingField?.id === m.id && editingField.field === "client" ? (
                          <div style={{ position: "relative" }}>
                            <input
                              ref={editInputRef}
                              type="text"
                              className="hist-inline-input"
                              value={editDraft}
                              autoFocus
                              onChange={(e) => handleClientInput(e.target.value)}
                              onKeyDown={handleEditKeyDown}
                              onBlur={() => setTimeout(commitEdit, 150)}
                            />
                            {clientSuggestions.length > 0 && (
                              <div className="hist-suggest">
                                {clientSuggestions.map((name, i) => (
                                  <div
                                    key={name}
                                    className={`hist-suggest-item${i === selectedSuggestion ? " active" : ""}`}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      selectSuggestion(name);
                                    }}
                                  >
                                    {name}
                                  </div>
                                ))}
                                {!knownClients.some(
                                  (c) => c.toLowerCase() === editDraft.trim().toLowerCase(),
                                ) &&
                                  editDraft.trim() && (
                                    <div
                                      className="hist-suggest-item new-client"
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        commitEdit();
                                      }}
                                    >
                                      + Create "{editDraft.trim()}"
                                    </div>
                                  )}
                              </div>
                            )}
                            {clientSuggestions.length === 0 &&
                              editDraft.trim() &&
                              !knownClients.some(
                                (c) => c.toLowerCase() === editDraft.trim().toLowerCase(),
                              ) && (
                                <div className="hist-suggest">
                                  <div
                                    className="hist-suggest-item new-client"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      commitEdit();
                                    }}
                                  >
                                    + Create "{editDraft.trim()}"
                                  </div>
                                </div>
                              )}
                          </div>
                        ) : (
                          <div
                            className="hist-client-name hist-editable"
                            onClick={(e) => startEditing(e, m.id, "client", m.client)}
                            title="Click to edit client"
                          >
                            {m.client}
                            {m.sharedBy && (
                              <span
                                className="shared-badge"
                                title={`Shared with you by ${m.sharedBy.name}${
                                  m.sharedAt
                                    ? ` · ${new Date(m.sharedAt).toLocaleDateString()}`
                                    : ""
                                }`}
                              >
                                ↗ Shared by {m.sharedBy.name.split(" ")[0]}
                              </span>
                            )}
                          </div>
                        )}
                        {m.participants && m.participants.length > 0 && (
                          <div className="hist-participants mono">
                            {m.participants.slice(0, 2).join(", ")}
                            {m.participants.length > 2 && ` +${m.participants.length - 2}`}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="hist-title" onClick={(e) => e.stopPropagation()}>
                      {editingField?.id === m.id && editingField.field === "title" ? (
                        <input
                          type="text"
                          className="hist-inline-input"
                          value={editDraft}
                          autoFocus
                          onChange={(e) => setEditDraft(e.target.value)}
                          onKeyDown={handleEditKeyDown}
                          onBlur={commitEdit}
                        />
                      ) : (
                        <span
                          className="hist-editable"
                          onClick={(e) => startEditing(e, m.id, "title", m.title)}
                          title="Click to edit title"
                        >
                          {m.title}
                        </span>
                      )}
                      {m.status === "live" && (
                        <span
                          style={{
                            marginLeft: 8,
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            background: "var(--gc-green-50, #e6f4ea)",
                            color: "var(--gc-green, #1e8e3e)",
                          }}
                        >
                          ● Live · resume
                        </span>
                      )}
                      {m.scheduledAt && new Date(m.scheduledAt) > new Date() && (
                        <span
                          style={{
                            marginLeft: 8,
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            background: "var(--gc-blue-50, #e8f0fe)",
                            color: "var(--gc-blue, #1a73e8)",
                          }}
                        >
                          Scheduled ·{" "}
                          {new Date(m.scheduledAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}{" "}
                          {new Date(m.scheduledAt).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })}
                        </span>
                      )}
                      {m.status === "draft" &&
                        !(m.scheduledAt && new Date(m.scheduledAt) > new Date()) && (
                          <span
                            style={{
                              marginLeft: 8,
                              padding: "2px 6px",
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: "uppercase",
                              background: "var(--gc-yellow-50, #fff7e0)",
                              color: "var(--gc-yellow, #b06000)",
                            }}
                          >
                            Draft
                          </span>
                        )}
                      {m.meetingType === "simulation" && (
                        <span
                          style={{
                            marginLeft: 8,
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            background: "var(--gc-purple-50, #f3e8fd)",
                            color: "var(--gc-purple, #7627bb)",
                          }}
                        >
                          Simulation
                        </span>
                      )}
                      {m.nextStep && (
                        <div className="hist-next">
                          <Chev size={12} /> {m.nextStep}
                        </div>
                      )}
                    </div>
                    <div>
                      <span className={`stage-pill stage-${m.stage.toLowerCase()}`}>
                        {m.meetingType
                          ? `${m.meetingType[0]!.toUpperCase()}${m.meetingType.slice(1)}`
                          : m.stage}
                      </span>
                    </div>
                    <div
                      className="hist-actions"
                      onClick={(e) => {
                        if (actionCount > 0) {
                          e.stopPropagation();
                          e.preventDefault();
                          setExpandedId(isExpanded ? null : m.id);
                        }
                      }}
                    >
                      {actionCount > 0 ? (
                        <span className="action-badge">{actionCount}</span>
                      ) : (
                        <span className="action-badge empty">—</span>
                      )}
                    </div>
                    {/* Labels column (replaces the old Hints column). Chips are
                        clickable to filter the list by that label. */}
                    <div className="hist-labels-cell" onClick={(e) => e.stopPropagation()}>
                      {m.tags && m.tags.length > 0 ? (
                        m.tags.slice(0, 4).map((t) => (
                          <button
                            key={t}
                            type="button"
                            className={`hist-label-chip${
                              selectedLabels.has(t.toLowerCase()) ? " on" : ""
                            }`}
                            title={`Filter by “${t}”`}
                            onClick={() => toggleLabel(t)}
                          >
                            {t}
                          </button>
                        ))
                      ) : (
                        <span style={{ color: "var(--text-4)", fontSize: 12 }}>—</span>
                      )}
                    </div>
                    <div className="hist-cta" onClick={(e) => e.stopPropagation()}>
                      <HistShareBtn meeting={m} />
                      {m.meetingType !== "simulation" &&
                        (m.status === "ended" || m.status === "summarized") && (
                          <button
                            type="button"
                            className="hist-sim-btn"
                            title="Continue this meeting from where it stopped"
                            onClick={() => continueMeeting(m.id)}
                          >
                            <Spark size={15} />
                          </button>
                        )}
                      {m.meetingType !== "simulation" &&
                        (m.status === "ended" || m.status === "summarized") && (
                          <button
                            type="button"
                            className="hist-sim-btn"
                            title="Simulate next meeting with this client"
                            onClick={() => startSimulation(m.id)}
                          >
                            <Brain size={15} />
                          </button>
                        )}
                      <button
                        type="button"
                        className="hist-delete-btn"
                        title="Delete meeting"
                        onClick={() => deleteMeeting(m.id, m.client)}
                      >
                        <Trash size={15} />
                      </button>
                    </div>
                  </div>
                  {isExpanded && meetingTasks.length > 0 && (
                    <div className="hist-expand-panel">
                      <div className="expand-header">Action items — {m.client}</div>
                      {meetingTasks.map((task) => (
                        <label
                          key={task.taskId}
                          className={`expand-task${task.done ? " done" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={task.done}
                            onChange={() => toggleTaskDone(task)}
                          />
                          <span className="expand-who">{task.who}</span>
                          <span className="expand-what">{task.what}</span>
                          <span className="expand-due mono">{task.due}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="hist-empty">
                <Inbox size={28} />
                <div className="hist-empty-title">
                  {scope === "shared"
                    ? "Nothing shared with you yet"
                    : scope === "drafts"
                      ? "No draft meetings"
                      : "No meetings match those filters"}
                </div>
                <div className="hist-empty-sub">
                  {scope === "shared"
                    ? "When teammates share a meeting with you, it'll show up here."
                    : scope === "drafts"
                      ? "Meetings you start but haven't begun will appear here."
                      : "Try clearing the search or stage filter."}
                </div>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                padding: "12px 0 4px",
              }}
            >
              <button
                type="button"
                className="pill-btn"
                disabled={pageClamped <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span style={{ fontSize: 13, color: "var(--text-3)" }}>
                Page {pageClamped} of {totalPages} · {filtered.length} meetings
              </span>
              <button
                type="button"
                className="pill-btn"
                disabled={pageClamped >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}

          {/* Load older meetings from the server (only "mine" is paged). Hidden while
              searching/filtering, which scan the already-loaded set. */}
          {moreCursor &&
            scope === "mine" &&
            filter === "all" &&
            typeFilter === "all" &&
            !search && (
              <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
                <button
                  type="button"
                  className="pill-btn"
                  disabled={loadingMore}
                  onClick={loadMoreHistory}
                >
                  {loadingMore ? "Loading…" : "Load more meetings"}
                </button>
              </div>
            )}
        </section>
          </div>

          {/* Secondary Intelligence Column */}
          <aside className="dash-col-secondary">
            <section className="card side-card">
              <div className="card-head">
                <div className="card-title">
                  <UserIcon size={16} /> Top accounts &amp; clients
                </div>
              </div>
              <ul className="client-list">
                {byClient.slice(0, 5).map(([name, count]) => (
                  <li key={name}>
                    <div className="client-bar-row">
                      <span className="client-name">{name}</span>
                      <span className="mono client-count">{count}</span>
                    </div>
                    <div className="client-bar">
                      <span style={{ width: `${(count / 5) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <section className="card side-card">
              <div className="card-head">
                <div className="card-title">
                  <Spark size={16} /> AI Coach Insights
                </div>
              </div>
              <ul className="insight-list">
                {insights.length > 0 ? (
                  insights.map((ins, i) => {
                    const iconStyle =
                      ins.icon === "up"
                        ? { background: "rgba(16, 185, 129, 0.15)", color: "#10B981", border: "1px solid rgba(16, 185, 129, 0.3)" }
                        : ins.icon === "warn"
                          ? { background: "rgba(245, 158, 11, 0.15)", color: "#F59E0B", border: "1px solid rgba(245, 158, 11, 0.3)" }
                          : { background: "rgba(99, 102, 241, 0.12)", color: "#a5b4fc", border: "1px solid rgba(99, 102, 241, 0.25)" };
                    const iconChar = ins.icon === "up" ? "↑" : ins.icon === "warn" ? "!" : "i";
                    return (
                      <li key={i}>
                        <div className="ins-icon" style={iconStyle}>
                          {iconChar}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>
                            {ins.title}
                          </div>
                          <div style={{ fontSize: 12, color: "#94A3B8", lineHeight: 1.4 }}>
                            {ins.detail}
                          </div>
                        </div>
                      </li>
                    );
                  })
                ) : (
                  <li>
                    <div
                      className="ins-icon"
                      style={{ background: "rgba(99, 102, 241, 0.12)", color: "#a5b4fc" }}
                    >
                      i
                    </div>
                    <div>Complete a few meetings to see personalized insights here.</div>
                  </li>
                )}
              </ul>
            </section>
          </aside>
        </div>
      </main>

      {bulkChatOpen && (
        <BulkChatModal
          meetings={allHistory
            .filter((m) => selectedIds.has(m.id))
            .map((m) => ({ id: m.id, client: m.client, title: m.title }))}
          onClose={() => setBulkChatOpen(false)}
        />
      )}

      {labelChatOpen && selectedLabels.size > 0 && (
        <BulkChatModal
          labels={allLabels.filter((l) => selectedLabels.has(l.toLowerCase()))}
          meetings={filtered
            .slice(0, 5)
            .map((m) => ({ id: m.id, client: m.client, title: m.title }))}
          onClose={() => setLabelChatOpen(false)}
        />
      )}

      <Modal
        open={quickOpen}
        onClose={() => {
          setQuickOpen(false);
          setIntelExpanded(false);
        }}
        title="Quick live meeting"
        width={
          quickKind === "external" && quickProfile
            ? intelExpanded
              ? "min(1320px, 98vw)"
              : "min(1040px, 96vw)"
            : 640
        }
      >
        <div className="modal-body" style={{ display: "grid", gap: 14 }}>
          {/* Step 1: internal or external? */}
          {quickKind === null ? (
            <>
              <p className="modal-sub" style={{ margin: 0 }}>
                Is this an internal meeting or a meeting with a customer?
              </p>
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  type="button"
                  className="pill-btn lg"
                  style={{ flex: 1, justifyContent: "center" }}
                  disabled={creating}
                  onClick={() => setQuickKind("internal")}
                >
                  Internal meeting
                </button>
                <button
                  type="button"
                  className="pill-btn lg primary"
                  style={{ flex: 1, justifyContent: "center" }}
                  disabled={creating}
                  onClick={() => setQuickKind("external")}
                >
                  External (with a customer)
                </button>
              </div>
            </>
          ) : quickKind === "internal" ? (
            <div style={{ display: "grid", gap: 14 }}>
              <p className="modal-sub" style={{ margin: 0 }}>
                Pick your role for this internal meeting, then go live.
              </p>
              <label className="qm-field">
                <span className="qm-label">Your role for this meeting</span>
                <select
                  className="setup-input"
                  value={effectiveQuickRole}
                  onChange={(e) => setQuickRole(e.target.value as UserRole)}
                >
                  {availableRoles.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
              <label className="qm-field">
                <span className="qm-label">Prep questions</span>
                <select
                  className="setup-input"
                  value={effectiveQKey}
                  onChange={(e) => setQuickQKey(e.target.value)}
                >
                  <option value="">My default questions</option>
                  {Object.keys(CUSTOM_QUESTION_PRESETS).map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </label>
              <label className="qm-field">
                <span className="qm-label">
                  Meeting type{" "}
                  <span style={{ color: "var(--text-3)", fontWeight: 400 }}>(optional)</span>
                </span>
                <select
                  className="setup-input"
                  value={quickFocus}
                  onChange={(e) => setQuickFocus(e.target.value as HintFocus | "")}
                >
                  <option value="">Standard (default)</option>
                  {(Object.keys(HINT_FOCUS_LABELS) as HintFocus[])
                    .filter((f) => f !== "standard")
                    .map((f) => (
                      <option key={f} value={f}>
                        {HINT_FOCUS_LABELS[f]}
                      </option>
                    ))}
                </select>
              </label>
              <label className="qm-field">
                <span className="qm-label">
                  Goal / topics{" "}
                  <span style={{ color: "var(--text-3)", fontWeight: 400 }}>(optional)</span>
                </span>
                <textarea
                  className="setup-input"
                  rows={3}
                  value={quickGoal}
                  onChange={(e) => setQuickGoal(e.target.value)}
                  placeholder="e.g. Internal sync on the Acme deal — next steps, blockers."
                />
              </label>
              <QuickContextDocs
                files={quickFiles}
                setFiles={setQuickFiles}
                links={quickLinks}
                setLinks={setQuickLinks}
              />
            </div>
          ) : (
            <div
              style={
                quickProfile
                  ? { display: "flex", gap: 18, alignItems: "flex-start" }
                  : { display: "grid", gap: 14 }
              }
            >
              {/* Left pane: company intel (Research / Billing / Growth) once researched.
                  Widens when expanded so the results + graphs get more room. */}
              {quickProfile && (
                <div
                  style={{
                    flex: intelExpanded ? "1 1 82%" : "1 1 56%",
                    minWidth: 0,
                    borderRight: "1px solid var(--border-soft, #e0e0e0)",
                    paddingRight: 18,
                  }}
                >
                  <CompanyReportPanel
                    companyName={quickName.trim()}
                    researchMarkdown={quickProfile.markdown || quickProfile.overview || ""}
                    researchLoading={quickResearching}
                    expanded={intelExpanded}
                    onToggleExpand={() => setIntelExpanded((v) => !v)}
                  />
                </div>
              )}

              {/* Right pane: the setup form. Shrinks when the intel pane is expanded. */}
              <div
                style={
                  quickProfile
                    ? {
                        flex: intelExpanded ? "1 1 18%" : "1 1 44%",
                        minWidth: 0,
                        display: "grid",
                        gap: 14,
                        alignContent: "start",
                      }
                    : { display: "grid", gap: 14 }
                }
              >
                <p className="modal-sub" style={{ margin: 0 }}>
                  Name the company to research it before you go live — or just start the meeting.
                </p>

                <label className="qm-field">
                  <span className="qm-label">Company</span>
                  <CompanyPicker
                    value={quickName}
                    onChange={(v) => {
                      setQuickName(v);
                      setQuickProfile(null);
                      setQuickError("");
                    }}
                    onSelect={(pick) => {
                      setQuickName(pick.name);
                      setQuickProfile(null);
                      setQuickError("");
                      if (pick.hasProfile && pick.slug) {
                        companiesApi
                          .get(pick.slug)
                          .then(setQuickProfile)
                          .catch(() => {});
                      } else {
                        // Chose a real company — research it now (fresh name + domain).
                        void quickResearch(false, pick.domain, pick.name);
                      }
                    }}
                    priorSuggestions={companySuggestions}
                    placeholder="e.g. Wix"
                    autoFocus
                  />
                </label>

                <label className="qm-field">
                  <span className="qm-label">Your role for this meeting</span>
                  <select
                    className="setup-input"
                    value={effectiveQuickRole}
                    onChange={(e) => setQuickRole(e.target.value as UserRole)}
                  >
                    {availableRoles.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="qm-field">
                  <span className="qm-label">
                    Goal / topics{" "}
                    <span style={{ color: "var(--text-3)", fontWeight: 400 }}>(optional)</span>
                  </span>
                  <textarea
                    className="setup-input"
                    rows={3}
                    value={quickGoal}
                    onChange={(e) => setQuickGoal(e.target.value)}
                    placeholder="e.g. Discuss BigQuery migration; pricing; data residency — SellSynq will coach on these and remind you of any you haven't covered."
                  />
                </label>

                <label className="qm-field">
                  <span className="qm-label">Prep questions</span>
                  <select
                    className="setup-input"
                    value={effectiveQKey}
                    onChange={(e) => setQuickQKey(e.target.value)}
                  >
                    <option value="">My default questions</option>
                    {Object.keys(CUSTOM_QUESTION_PRESETS).map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="qm-field">
                  <span className="qm-label">
                    Meeting type{" "}
                    <span style={{ color: "var(--text-3)", fontWeight: 400 }}>(optional)</span>
                  </span>
                  <select
                    className="setup-input"
                    value={quickFocus}
                    onChange={(e) => setQuickFocus(e.target.value as HintFocus | "")}
                  >
                    <option value="">Standard (default)</option>
                    {(Object.keys(HINT_FOCUS_LABELS) as HintFocus[])
                      .filter((f) => f !== "standard")
                      .map((f) => (
                        <option key={f} value={f}>
                          {HINT_FOCUS_LABELS[f]}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="qm-field">
                  <span className="qm-label">
                    Client industry{" "}
                    <span style={{ color: "var(--text-3)", fontWeight: 400 }}>(optional)</span>
                  </span>
                  <input
                    type="text"
                    className="setup-input"
                    list="quick-industry-options"
                    value={quickIndustry}
                    onChange={(e) => setQuickIndustry(e.target.value)}
                    placeholder="e.g. Financial services, Healthcare, Retail, Gaming…"
                  />
                  <datalist id="quick-industry-options">
                    {[
                      "Financial services",
                      "Healthcare & life sciences",
                      "Retail & e-commerce",
                      "Manufacturing",
                      "Media & entertainment",
                      "Gaming",
                      "Telecommunications",
                      "Public sector & government",
                      "Education",
                      "Technology / software",
                      "Automotive",
                      "Energy & utilities",
                      "Travel & hospitality",
                      "Transportation & logistics",
                    ].map((i) => (
                      <option key={i} value={i} />
                    ))}
                  </datalist>
                </label>

                <QuickContextDocs
                  files={quickFiles}
                  setFiles={setQuickFiles}
                  links={quickLinks}
                  setLinks={setQuickLinks}
                />

                {quickError && (
                  <div
                    role="alert"
                    style={{
                      fontSize: 13,
                      color: "var(--gc-red, #d93025)",
                      background: "var(--gc-red-50, #fce8e6)",
                      border: "1px solid var(--gc-red, #d93025)",
                      borderRadius: 8,
                      padding: "8px 10px",
                    }}
                  >
                    {quickError}
                  </div>
                )}

                {quickResearching && !quickProfile && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      color: "var(--text-3)",
                      fontSize: 13,
                    }}
                  >
                    <Spinner size={16} /> Researching {quickName.trim()}…
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {quickKind === "internal" && (
          <div className="modal-foot">
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setQuickKind(null)}
              disabled={creating}
            >
              Back
            </button>
            <button
              type="button"
              className="pill-btn primary"
              onClick={() => quickStart("internal")}
              disabled={creating}
            >
              {creating ? "Starting…" : "Start internal meeting"}
            </button>
          </div>
        )}

        {quickKind === "external" && (
          <div className="modal-foot">
            {quickKind === "external" && (
              <>
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => quickStart()}
                  disabled={creating}
                  title="Start without a company — you can add it after the meeting"
                >
                  Skip
                </button>
                <button
                  type="button"
                  className="pill-btn"
                  onClick={() => quickResearch(false)}
                  disabled={!quickName.trim() || quickResearching}
                >
                  {quickResearching ? "Researching…" : quickProfile ? "Re-review" : "Get review"}
                </button>
              </>
            )}
            <button
              type="button"
              className="pill-btn primary"
              onClick={() => quickStart()}
              disabled={creating || (quickKind === "external" && !quickName.trim())}
            >
              Start meeting
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
